import api from "../auth/api";
import type {
  GetIncomeStatementQuery,
  GetIncomeStatementResponse,
  GetBalanceSheetQuery,
  GetBalanceSheetResponse,
  GetCashFlowStatementQuery,
  GetCashFlowStatementResponse,
  GetRatiosQuery,
  GetRatiosResponse,
  GetFinancialRatiosQuery,
  GetFinancialRatiosResponse,
  GetEarningsQuery,
  GetEarningsResponse,
  GetEarningsCallTranscriptQuery,
  GetEarningsCallTranscriptResponse,
  GetAnalystRatingQuery,
  GetAnalystRatingResponse,
  GetAnalystRatingHistoryQuery,
  GetAnalystRatingHistoryResponse,
  GetDividendSummaryQuery,
  GetDividendSummaryResponse,
  GetDividendHistoryQuery,
  GetDividendHistoryResponse,
  GetBeneficialOwnershipQuery,
  GetBeneficialOwnershipResponse,
  GetHistoricalPriceEodQuery,
  GetHistoricalPriceEodResponse,
  BulkProfileRequest,
  BulkProfileResponse,
  GetInsiderTradingQuery,
  GetInsiderTradingResponse,
  RankingScoreRequest,
  RankingScoreResponse,
  RatingRequest,
  RatingResponse,
  DividendsBulkRequest,
  DividendsBulkResponse,
  BulkIncomeStatementRequest,
  BulkIncomeStatementResponse,
  BulkBalanceSheetRequest,
  BulkBalanceSheetResponse,
  BulkCashFlowStatementRequest,
  BulkCashFlowStatementResponse,
  BulkAnnualizedReturnRequest,
  BulkAnnualizedReturnResponse,
  GetFinancialAnalyticsQuery,
  GetFinancialAnalyticsResponse,
  GetFinancialAnalyticsListQuery,
  GetFinancialAnalyticsListResponse,
  GetMarginOfSafetyQuery,
  GetMarginOfSafetyResponse,
  GetSecFilingsBySectorQuery,
  GetSecFilingsBySectorResponse,
  GetSwotScoreQuery,
  GetSwotScoreResponse,
  GetKeyStatsQuery,
  GetKeyStatsResponse,
  GetStockNewsQuery,
  GetStockNewsResponse,
  GetCompanyProfileQuery,
  GetCompanyProfileResponse,
  GetQuotesResponse,
  GetHistoricalDividendsQuery,
  GetHistoricalDividendsResponse,
  GetHistoricalSectorPerformanceQuery,
  GetHistoricalSectorPerformanceResponse,
  GetStockAnalysisQuery,
  GetStockAnalysisResponse,
  GetAnalystDescriptionQuery,
  GetAnalystDescriptionResponse,
  GetAnalystRatingDescriptionQuery,
  GetAnalystRatingDescriptionResponse,
  GetSnaptradeAccountHoldingsResponse,
  GetSnaptradeAccountsResponse,
  RefreshSnaptradeAccountResponse,
  SnaptradeLoginResponse,
  SnaptradeHolding,
  SnaptradeAccount,
  SearchTradableSymbolsResponse,
  GetTradingBalancesResponse,
  PreviewTradeOrderResponse,
  PlaceTradeOrderBody,
  PlaceTradeOrderResponse,
  ListTradeOrdersQuery,
  ListTradeOrdersResponse,
} from "./financial-integration.type";

// Get income statement
export const getIncomeStatement = async (
  query: GetIncomeStatementQuery,
): Promise<GetIncomeStatementResponse> => {
  const response = await api.get<GetIncomeStatementResponse>(
    "/financial-integration/income-statement",
    {
      params: query,
    },
  );
  return response.data;
};

// Get beneficial ownership
export const getBeneficialOwnership = async (
  query: GetBeneficialOwnershipQuery,
): Promise<GetBeneficialOwnershipResponse> => {
  const response = await api.get<GetBeneficialOwnershipResponse>(
    "/financial-integration/beneficial-ownership",
    {
      params: query,
    },
  );
  return response.data;
};

// Get historical price EOD
export const getHistoricalPriceEod = async (
  query: GetHistoricalPriceEodQuery,
): Promise<GetHistoricalPriceEodResponse> => {
  const response = await api.get<GetHistoricalPriceEodResponse>(
    "/financial-integration/historical-price-eod",
    {
      params: query,
    },
  );
  return response.data;
};

// Get bulk profile
export const getBulkProfile = async (
  payload: BulkProfileRequest,
): Promise<BulkProfileResponse> => {
  const response = await api.post<BulkProfileResponse>(
    "/financial-analytics/bulk/profile",
    payload,
  );
  return response.data;
};

// Get ranking score
export const getRankingScore = async (
  payload: RankingScoreRequest,
): Promise<RankingScoreResponse> => {
  const response = await api.post<RankingScoreResponse>(
    "/financial-analytics/bulk/ranking-score",
    payload,
  );
  return response.data;
};

// Get rating
export const getRating = async (
  payload: RatingRequest,
): Promise<RatingResponse> => {
  const response = await api.post<RatingResponse>(
    "/financial-analytics/bulk/rating",
    payload,
  );
  return response.data;
};

// Get dividends bulk
export const getDividendsBulk = async (
  payload: DividendsBulkRequest,
): Promise<DividendsBulkResponse> => {
  const response = await api.post<DividendsBulkResponse>(
    "/financial-analytics/bulk/dividends",
    payload,
  );
  return response.data;
};

// Get annualized return bulk
export const getBulkAnnualizedReturn = async (
  payload: BulkAnnualizedReturnRequest,
): Promise<BulkAnnualizedReturnResponse> => {
  const response = await api.post<BulkAnnualizedReturnResponse>(
    "/financial-analytics/bulk/annualized-return",
    payload,
  );
  return response.data;
};

// Get financial analytics by symbol
export const getFinancialAnalytics = async (
  query: GetFinancialAnalyticsQuery,
): Promise<GetFinancialAnalyticsResponse> => {
  const response = await api.get<GetFinancialAnalyticsResponse>(
    `/financial-analytics/${encodeURIComponent(query.symbol)}`,
  );
  return response.data;
};

// Get financial analytics list
export const getFinancialAnalyticsList = async (
  query: GetFinancialAnalyticsListQuery = {},
): Promise<GetFinancialAnalyticsListResponse> => {
  const response = await api.get<GetFinancialAnalyticsListResponse>(
    "/financial-analytics",
    {
      params: {
        limit: query.limit ?? 0,
        offset: query.offset,
      },
    },
  );
  return response.data;
};

// Get income statement bulk
export const getBulkIncomeStatement = async (
  payload: BulkIncomeStatementRequest,
): Promise<BulkIncomeStatementResponse> => {
  const response = await api.post<BulkIncomeStatementResponse>(
    "/financial-analytics/bulk/income-statement",
    payload,
  );
  return response.data;
};

// Get balance sheet bulk
export const getBulkBalanceSheet = async (
  payload: BulkBalanceSheetRequest,
): Promise<BulkBalanceSheetResponse> => {
  const response = await api.post<BulkBalanceSheetResponse>(
    "/financial-analytics/bulk/balance-sheet",
    payload,
  );
  return response.data;
};

// Get cash flow statement bulk
export const getBulkCashFlowStatement = async (
  payload: BulkCashFlowStatementRequest,
): Promise<BulkCashFlowStatementResponse> => {
  const response = await api.post<BulkCashFlowStatementResponse>(
    "/financial-analytics/bulk/cash-flow-statement",
    payload,
  );
  return response.data;
};

// Get insider trading
export const getInsiderTrading = async (
  query: GetInsiderTradingQuery,
): Promise<GetInsiderTradingResponse> => {
  const response = await api.get<GetInsiderTradingResponse>(
    "/financial-integration/insider-trading",
    {
      params: query,
    },
  );
  return response.data;
};

// Get balance sheet
export const getBalanceSheet = async (
  query: GetBalanceSheetQuery,
): Promise<GetBalanceSheetResponse> => {
  const response = await api.get<GetBalanceSheetResponse>(
    "/financial-integration/balance-sheet",
    {
      params: query,
    },
  );
  return response.data;
};

// Get cash flow statement
export const getCashFlowStatement = async (
  query: GetCashFlowStatementQuery,
): Promise<GetCashFlowStatementResponse> => {
  const response = await api.get<GetCashFlowStatementResponse>(
    "/financial-integration/cash-flow-statement",
    {
      params: query,
    },
  );
  return response.data;
};

// Get ratios
export const getRatios = async (
  query: GetRatiosQuery,
): Promise<GetRatiosResponse> => {
  const response = await api.get<GetRatiosResponse>(
    "/financial-integration/ratios",
    {
      params: query,
    },
  );
  return response.data;
};

// Get financial ratios
export const getFinancialRatios = async (
  query: GetFinancialRatiosQuery,
): Promise<GetFinancialRatiosResponse> => {
  const { tabAccessToken, ...params } = query;
  const response = await api.get<GetFinancialRatiosResponse>(
    "/financial-integration/financial-ratios",
    {
      params,
      headers: tabAccessToken
        ? { "X-Tab-Access-Token": tabAccessToken }
        : undefined,
    },
  );
  return response.data;
};

// Get earnings
export const getEarnings = async (
  query: GetEarningsQuery,
): Promise<GetEarningsResponse> => {
  const response = await api.get<GetEarningsResponse>(
    "/financial-integration/earnings",
    {
      params: query,
    },
  );
  return response.data;
};

// Get earnings call transcripts
export const getEarningsCallTranscripts = async (
  query: GetEarningsCallTranscriptQuery,
): Promise<GetEarningsCallTranscriptResponse> => {
  const response = await api.get<GetEarningsCallTranscriptResponse>(
    "/financial-integration/earning-call-transcript",
    {
      params: query,
    },
  );
  return response.data;
};

// Get analyst rating
export const getAnalystRating = async (
  query: GetAnalystRatingQuery,
): Promise<GetAnalystRatingResponse> => {
  const { tabAccessToken, ...params } = query;
  const response = await api.get<GetAnalystRatingResponse>(
    "/financial-integration/analyst-rating",
    {
      params,
      headers: tabAccessToken
        ? { "X-Tab-Access-Token": tabAccessToken }
        : undefined,
    },
  );
  return response.data;
};

// Get analyst rating history
export const getAnalystRatingHistory = async (
  query: GetAnalystRatingHistoryQuery,
): Promise<GetAnalystRatingHistoryResponse> => {
  const { tabAccessToken, ...params } = query;
  const response = await api.get<GetAnalystRatingHistoryResponse>(
    "/financial-integration/analyst-rating-history",
    {
      params,
      headers: tabAccessToken
        ? { "X-Tab-Access-Token": tabAccessToken }
        : undefined,
    },
  );
  return response.data;
};

// Get dividend summary
export const getDividendSummary = async (
  query: GetDividendSummaryQuery,
): Promise<GetDividendSummaryResponse> => {
  const response = await api.get<GetDividendSummaryResponse>(
    "/financial-integration/dividents-summary",
    {
      params: query,
    },
  );
  return response.data;
};

// Get dividend history
export const getDividendHistory = async (
  query: GetDividendHistoryQuery,
): Promise<GetDividendHistoryResponse> => {
  const response = await api.get<GetDividendHistoryResponse>(
    "/financial-integration/dividends",
    {
      params: query,
    },
  );
  return response.data;
};

export const getMarginOfSafety = async (
  query: GetMarginOfSafetyQuery,
): Promise<GetMarginOfSafetyResponse> => {
  const { symbol, tabAccessToken } = query;
  const response = await api.get<GetMarginOfSafetyResponse>(
    "/financial-integration/margin-of-safety",
    {
      params: { symbol },
      headers: tabAccessToken
        ? { "X-Tab-Access-Token": tabAccessToken }
        : undefined,
    },
  );

  return response.data;
};

// Get SEC filings by sector
export const getSecFilingsBySector = async (
  query: GetSecFilingsBySectorQuery,
): Promise<GetSecFilingsBySectorResponse> => {
  const response = await api.get<GetSecFilingsBySectorResponse>(
    "/financial-integration/sec-filings-by-sector",
    {
      params: {
        ticker: query.ticker,
        year: query.year,
      },
    },
  );
  return response.data;
};

// Get SWOT score
export const getSwotScore = async (
  query: GetSwotScoreQuery,
): Promise<GetSwotScoreResponse> => {
  const { tabAccessToken, ...params } = query;
  const response = await api.get<GetSwotScoreResponse>(
    "/financial-integration/swot-score",
    {
      params: { symbol: params.symbol },
      headers: tabAccessToken
        ? { "X-Tab-Access-Token": tabAccessToken }
        : undefined,
    },
  );
  return response.data;
};

// Get key stats
export const getKeyStats = async (
  query: GetKeyStatsQuery,
): Promise<GetKeyStatsResponse> => {
  const { tabAccessToken, ...params } = query;
  const response = await api.get<GetKeyStatsResponse>(
    "/financial-integration/key-stats",
    {
      params: { symbol: params.symbol },
      headers: tabAccessToken
        ? { "X-Tab-Access-Token": tabAccessToken }
        : undefined,
    },
  );
  return response.data;
};

// Get stock news
export const getStockNews = async (
  query: GetStockNewsQuery,
): Promise<GetStockNewsResponse> => {
  const response = await api.get<GetStockNewsResponse>(
    "/financial-integration/stock-news",
    {
      params: {
        symbols: query.symbols,
        page: query.page ?? 0,
        limit: query.limit ?? 10,
      },
    },
  );
  return response.data;
};

// Get company profile
export const getCompanyProfile = async (
  query: GetCompanyProfileQuery,
): Promise<GetCompanyProfileResponse> => {
  const { tabAccessToken, ...params } = query;
  const response = await api.get<GetCompanyProfileResponse>(
    "/financial-integration/company-profile",
    {
      params: { symbol: params.symbol },
      headers: tabAccessToken
        ? { "X-Tab-Access-Token": tabAccessToken }
        : undefined,
    },
  );
  return response.data;
};

// Get quotes
export const getQuotes = async (): Promise<GetQuotesResponse> => {
  const response = await api.get<GetQuotesResponse>(
    "/financial-integration/quotes",
  );
  return response.data;
};

// Get historical dividends
export const getHistoricalDividends = async (
  query: GetHistoricalDividendsQuery,
): Promise<GetHistoricalDividendsResponse> => {
  const response = await api.get<GetHistoricalDividendsResponse>(
    "/financial-integration/historical-dividends",
    {
      params: {
        symbol: query.symbol,
        limit: query.limit,
      },
    },
  );
  return response.data;
};

// Get historical sector performance
export const getHistoricalSectorPerformance = async (
  query: GetHistoricalSectorPerformanceQuery,
): Promise<GetHistoricalSectorPerformanceResponse> => {
  const response = await api.get<GetHistoricalSectorPerformanceResponse>(
    "/financial-integration/historical-sector-performance",
    {
      params: {
        sector: query.sector,
        from: query.from,
        to: query.to,
      },
    },
  );
  return response.data;
};

// Get stock analysis
export const getStockAnalysis = async (
  query: GetStockAnalysisQuery,
): Promise<GetStockAnalysisResponse> => {
  const { tabAccessToken, ...params } = query;
  const response = await api.get<GetStockAnalysisResponse>(
    "/financial-integration/stock-analysis",
    {
      params: { symbol: params.symbol },
      headers: tabAccessToken
        ? { "X-Tab-Access-Token": tabAccessToken }
        : undefined,
    },
  );
  return response.data;
};

// Get analyst description
export const getAnalystDescription = async (
  query: GetAnalystDescriptionQuery,
): Promise<GetAnalystDescriptionResponse> => {
  const { tabAccessToken, ...params } = query;
  const response = await api.get<GetAnalystDescriptionResponse>(
    "/financial-integration/analyst-description",
    {
      params: { symbol: params.symbol },
      headers: tabAccessToken
        ? { "X-Tab-Access-Token": tabAccessToken }
        : undefined,
    },
  );
  return response.data;
};

// Get analyst rating description
export const getAnalystRatingDescription = async (
  query: GetAnalystRatingDescriptionQuery,
): Promise<GetAnalystRatingDescriptionResponse> => {
  const { tabAccessToken, ...params } = query;
  const response = await api.get<GetAnalystRatingDescriptionResponse>(
    "/financial-integration/analyst-rating-description",
    {
      params: { symbol: params.symbol },
      headers: tabAccessToken
        ? { "X-Tab-Access-Token": tabAccessToken }
        : undefined,
    },
  );
  return response.data;
};

const normalizeSnaptradeAccountsResponse = (
  payload:
    | GetSnaptradeAccountsResponse
    | SnaptradeAccount[]
    | SnaptradeAccount
    | null
    | undefined
): GetSnaptradeAccountsResponse => {
  if (payload && typeof payload === "object" && "data" in payload) {
    const envelope = payload as GetSnaptradeAccountsResponse;
    const { data } = envelope;

    if (Array.isArray(data)) {
      return envelope;
    }

    return {
      success: envelope.success ?? true,
      data: data == null ? [] : [data as SnaptradeAccount],
      error: envelope.error ?? "",
      message: envelope.message ?? "",
    };
  }

  if (Array.isArray(payload)) {
    const [only] = payload;

    if (
      payload.length === 1 &&
      only &&
      typeof only === "object" &&
      "data" in only &&
      !(
        "id" in only &&
        "name" in only &&
        typeof (only as SnaptradeAccount).id === "string"
      )
    ) {
      return normalizeSnaptradeAccountsResponse(
        only as unknown as GetSnaptradeAccountsResponse,
      );
    }

    return {
      success: true,
      data: payload as SnaptradeAccount[],
      error: "",
      message: "",
    };
  }

  return {
    success: true,
    data: payload ? [payload as SnaptradeAccount] : [],
    error: "",
    message: "",
  };
};

const normalizeSnaptradeHoldingsResponse = (
  payload:
    | GetSnaptradeAccountHoldingsResponse
    | SnaptradeHolding[]
    | SnaptradeHolding
    | null
    | undefined
): GetSnaptradeAccountHoldingsResponse => {
  if (payload && typeof payload === "object" && "data" in payload) {
    const envelope = payload as GetSnaptradeAccountHoldingsResponse;
    const { data } = envelope;

    if (Array.isArray(data)) {
      return envelope;
    }

    return {
      success: envelope.success ?? true,
      data: data == null ? [] : [data as SnaptradeHolding],
      error: envelope.error ?? "",
      message: envelope.message ?? "",
    };
  }

  if (Array.isArray(payload)) {
    const [only] = payload;

    if (
      payload.length === 1 &&
      only &&
      typeof only === "object" &&
      "data" in only &&
      !(
        "id" in only &&
        "ticker" in only &&
        typeof (only as SnaptradeHolding).id === "string"
      )
    ) {
      return normalizeSnaptradeHoldingsResponse(
        only as unknown as GetSnaptradeAccountHoldingsResponse,
      );
    }

    return {
      success: true,
      data: payload as SnaptradeHolding[],
      error: "",
      message: "",
    };
  }

  return {
    success: true,
    data: payload ? [payload as SnaptradeHolding] : [],
    error: "",
    message: "",
  };
};

export const getSnaptradeAccounts = async (options?: {
  updateCache?: boolean;
}): Promise<GetSnaptradeAccountsResponse> => {
  const response = await api.get<
    GetSnaptradeAccountsResponse | SnaptradeAccount[] | SnaptradeAccount
  >("/financial-integration/snaptrade/accounts", {
    params: options?.updateCache ? { update_cache: true } : undefined,
  });

  return normalizeSnaptradeAccountsResponse(response.data);
};

export const getSnaptradeAccountHoldings = async (
  accountId: string,
  options?: { updateCache?: boolean; page?: number; limit?: number },
): Promise<GetSnaptradeAccountHoldingsResponse> => {
  const response = await api.get<
    | GetSnaptradeAccountHoldingsResponse
    | SnaptradeHolding[]
    | SnaptradeHolding
  >(`/financial-integration/snaptrade/accounts/${accountId}/holdings`, {
    params: {
      ...(options?.updateCache ? { update_cache: true } : {}),
      ...(options?.page ? { page: options.page } : {}),
      ...(options?.limit ? { limit: options.limit } : {}),
    },
  });

  return normalizeSnaptradeHoldingsResponse(response.data);
};

export const getSnaptradeLoginLink = async (): Promise<SnaptradeLoginResponse> => {
  const response = await api.post<SnaptradeLoginResponse>(
    "/financial-integration/snaptrade/login"
  );

  return response.data;
};

/**
 * Trigger an async holdings refresh for the SnapTrade connection
 * that owns the given account.
 */
export const refreshSnaptradeAccount = async (
  accountId: string,
): Promise<RefreshSnaptradeAccountResponse> => {
  const response = await api.post<RefreshSnaptradeAccountResponse>(
    `/financial-integration/snaptrade/accounts/${accountId}/refresh`,
  );

  return response.data;
};

export const searchTradableSymbols = async (
  accountId: string,
  query: string,
): Promise<SearchTradableSymbolsResponse> => {
  const response = await api.get<SearchTradableSymbolsResponse>(
    `/financial-integration/trading/accounts/${accountId}/symbols`,
    { params: { q: query } },
  );
  return response.data;
};

export const getTradingBalances = async (
  accountId: string,
): Promise<GetTradingBalancesResponse> => {
  const response = await api.get<GetTradingBalancesResponse>(
    `/financial-integration/trading/accounts/${accountId}/balances`,
  );
  return response.data;
};

export const previewTradeOrder = async (
  accountId: string,
  body: Omit<PlaceTradeOrderBody, "idempotency_key" | "preview_id">,
): Promise<PreviewTradeOrderResponse> => {
  const response = await api.post<PreviewTradeOrderResponse>(
    `/financial-integration/trading/accounts/${accountId}/orders/preview`,
    body,
  );
  return response.data;
};

export const placeTradeOrder = async (
  accountId: string,
  body: PlaceTradeOrderBody,
): Promise<PlaceTradeOrderResponse> => {
  const response = await api.post<PlaceTradeOrderResponse>(
    `/financial-integration/trading/accounts/${accountId}/orders`,
    body,
  );
  return response.data;
};

export const listTradeOrders = async (
  accountId: string,
  query: ListTradeOrdersQuery = {},
): Promise<ListTradeOrdersResponse> => {
  const response = await api.get<ListTradeOrdersResponse>(
    `/financial-integration/trading/accounts/${accountId}/orders`,
    { params: query },
  );
  return response.data;
};

export const cancelTradeOrder = async (
  orderId: string,
): Promise<PlaceTradeOrderResponse> => {
  const response = await api.delete<PlaceTradeOrderResponse>(
    `/financial-integration/trading/orders/${orderId}`,
  );
  return response.data;
};
